/**
 * la-resource-filter.js — "Browse All Resources" page.
 *
 * A standalone filter/browse page driven by the life-event taxonomy, with the
 * filter rail on the LEFT. It renders the SAME content cards as the life-journey
 * filter and the life-event pages (via window.LACards), and calls the SAME
 * POST {proxy}/clients/filter endpoint.
 *
 * Filter dimensions (all optional; each supplied dimension ANDs, ids within a
 * dimension OR):
 *   - By topic        -> topic_ids       (topic_tag)      GET /clients/topics
 *   - By life event   -> life_event_ids  (life_event)     GET /clients/life-events
 *       nested sub-topics -> sub_topic_ids (sub_topic)    (from life-events payload)
 *   - By resource type-> content_type    (articles/products)
 *
 * Data-driven: the sidebar is built from whatever the taxonomy endpoints return,
 * so groups grow automatically as content is tagged. Empty groups show a muted note.
 */
(function () {
  "use strict";

  var CONTENT_TYPES = ["articles", "products"];

  // ---- proxy + id helpers (parity with la-life-events.js) ------------------
  function proxyUrl(path) {
    if (window.ENV_CONFIG && typeof window.ENV_CONFIG.getProxyUrl === "function") {
      return window.ENV_CONFIG.getProxyUrl(path);
    }
    return "/apps" + path;
  }

  function toGid(id) {
    if (!id) return id;
    id = String(id).trim();
    return id.indexOf("gid://") === 0 ? id : "gid://shopify/Metaobject/" + id;
  }

  function bareId(id) {
    if (!id) return id;
    return String(id).replace("gid://shopify/Metaobject/", "");
  }

  function debugOn() {
    return /[?&]la_debug=1/.test(window.location.search);
  }

  // ---- filter request (parity with la-life-events.js fetchContent) ---------
  async function postFilter(body) {
    var endpoint = proxyUrl("/clients/filter");
    var res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("HTTP " + res.status + " from " + endpoint);
    var data;
    try {
      data = await res.json();
    } catch (e) {
      throw new Error("Non-JSON response from " + endpoint + " (HTTP " + res.status + ")");
    }
    if (!data || data.status !== 0 || !data.response) {
      var msg = (data && data.response && data.response.error) || "status " + (data && data.status);
      throw new Error("Filter error: " + msg);
    }
    return data.response;
  }

  async function getJson(path) {
    var res = await fetch(proxyUrl(path), { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("HTTP " + res.status + " from " + path);
    var data = await res.json();
    return (data && data.response) || {};
  }

  // ---- placeholder / logo / url helpers (parity with la-life-events.js) ----
  function isValidImg(u) {
    return typeof u === "string" && u.trim().length > 0;
  }
  function categoryMap() {
    var map = {};
    (window.LA_LE_CATEGORIES || []).forEach(function (c) { map[c.id] = c; });
    return map;
  }
  function l1TitleForItem(item) {
    var raw = item.category && item.category.id ? item.category.id : item.category;
    if (!raw) return null;
    var map = categoryMap();
    var cat = map[bareId(raw)];
    if (!cat) return null;
    var l1 = cat.level === "l1" ? cat : (cat.parent ? map[cat.parent] : null);
    return l1 ? l1.title : null;
  }
  function placeholderFor(item) {
    var cfg = window.LA_LE_PLACEHOLDERS || {};
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type === "product" || type === "offering") return cfg.offeringPlaceholder || null;
    var title = l1TitleForItem(item);
    if (title && cfg.category1Name && title === cfg.category1Name && cfg.category1ArticlePlaceholder) return cfg.category1ArticlePlaceholder;
    if (title && cfg.category2Name && title === cfg.category2Name && cfg.category2ArticlePlaceholder) return cfg.category2ArticlePlaceholder;
    return cfg.articlePlaceholder || null;
  }
  function toRelativeUrl(url) {
    if (!url || url === "#") return url || "#";
    try {
      var u = new URL(url, window.location.origin);
      return (u.pathname || "/") + u.search + u.hash;
    } catch (e) {
      return url;
    }
  }
  var _normProfiles;
  function normName(s) {
    return String(s == null ? "" : s).toLowerCase().replace(/[‘’'"]/g, "").replace(/\s+/g, " ").trim();
  }
  function normalizedProfiles() {
    if (_normProfiles) return _normProfiles;
    _normProfiles = {};
    var p = window.participantsProfiles || {};
    Object.keys(p).forEach(function (k) { _normProfiles[normName(k)] = p[k]; });
    return _normProfiles;
  }
  function participantLogoFor(item) {
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type !== "article") return null;
    var author = item.author || "";
    if (!author) return null;
    var profiles = window.participantsProfiles || {};
    var np = normalizedProfiles();
    var na = normName(author);
    var profile = profiles[author] || np[na];
    if (!profile && na.charAt(na.length - 1) === "s") profile = np[na.slice(0, -1)];
    if (!profile && na.charAt(na.length - 1) !== "s") profile = np[na + "s"];
    return profile ? profile.company_logo : null;
  }
  function cardOpts(item) {
    var opts = {};
    if (!isValidImg(item.preview_image_url) && !isValidImg(item.featured_image) && !isValidImg(item.image_url)) {
      var ph = placeholderFor(item);
      if (ph) opts.imageUrl = ph;
    }
    var type = item.content_type || (item.price ? "offering" : "article");
    if (type !== "product" && type !== "offering") {
      var af = placeholderFor(item);
      if (af) opts.imageFallback = af;
    }
    var logo = participantLogoFor(item);
    if (logo) opts.authorLogo = logo;
    return opts;
  }
  function renderCardsHtml(items) {
    if (!window.LACards || typeof window.LACards.render !== "function") return "";
    return items
      .map(function (item) {
        item.url = toRelativeUrl(item.url);
        return window.LACards.render(item, cardOpts(item));
      })
      .join("");
  }

  // ---- favorites (parity with la-life-events.js) ---------------------------
  async function toggleFavorite(btn) {
    var id = btn.dataset.id;
    var wasFav = btn.classList.contains("is-favorited");
    var keyname = (btn.dataset.type === "offering" || (id && id.indexOf("Product") !== -1)) ? "product_id" : "article_id";
    var payload = wasFav
      ? { remove: true }
      : {
          article_title: btn.dataset.title,
          article_url: btn.dataset.url,
          image_url: btn.dataset.image,
          object_type: btn.dataset.type || "article",
        };
    payload[keyname] = id;
    btn.classList.toggle("is-favorited");
    btn.setAttribute("aria-pressed", wasFav ? "false" : "true");
    try {
      var res = await fetch(proxyUrl("/clients/favorites"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      var data = await res.json();
      var ok = data && (data.status === 0 || data.success === true || !data.error);
      if (!ok) throw new Error("favorite failed");
    } catch (err) {
      btn.classList.toggle("is-favorited");
      btn.setAttribute("aria-pressed", wasFav ? "true" : "false");
    }
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  var SORTS = {
    recommended: { sort_field: "createdAt", sort_direction: "desc" },
    newest: { sort_field: "createdAt", sort_direction: "desc" },
    title_az: { sort_field: "title", sort_direction: "asc" },
    title_za: { sort_field: "title", sort_direction: "desc" },
  };

  var CHEVRON = '<svg class="la-rf__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';

  // ---- controller ----------------------------------------------------------
  function LAResourceFilter(root) {
    this.root = root;
    this.groupsEl = root.querySelector("[data-la-rf-groups]");
    this.cardsEl = root.querySelector("[data-la-rf-cards]");
    this.countEl = root.querySelector("[data-la-rf-count]");
    this.emptyEl = root.querySelector("[data-la-rf-empty]");
    this.paginationEl = root.querySelector("[data-la-rf-pagination]");
    this.loadMoreBtn = root.querySelector("[data-la-rf-loadmore]");
    this.sortEl = root.querySelector("[data-la-rf-sort]");
    this.clearEl = root.querySelector("[data-la-rf-clear]");

    // Mobile bottom-sheet controls (no-ops on desktop, where they stay hidden).
    this.sidebarEl = root.querySelector("[data-la-rf-sidebar]");
    this.triggerEl = root.querySelector("[data-la-rf-open]");
    this.backdropEl = root.querySelector("[data-la-rf-backdrop]");
    this.doneEl = root.querySelector("[data-la-rf-done]");
    this.handleEl = root.querySelector("[data-la-rf-handle]");
    this.activeCountEl = root.querySelector("[data-la-rf-active-count]");
    this.sheetOpen = false;

    this.pageSize = parseInt(root.dataset.pageSize, 10) || 12;
    this.types = new Set();       // content_type: "articles" / "products"
    this.lifeEvents = new Set();  // life_event GIDs
    this.subTopics = new Set();   // sub_topic GIDs
    this.topics = new Set();      // topic_tag GIDs
    this.sortKey = "recommended";

    this.taxonomy = { lifeEvents: [], topics: [] };
    this.offset = 0;
    this.total = null;
    this.loading = false;
  }

  LAResourceFilter.prototype.init = async function () {
    var self = this;
    this.bindStaticEvents();
    this.renderSkeletonCards();
    try {
      var results = await Promise.all([
        getJson("/clients/life-events?limit=100").catch(function () { return {}; }),
        getJson("/clients/topics?in_use=false").catch(function () { return {}; }),
      ]);
      this.taxonomy.lifeEvents = (results[0] && results[0].results) || [];
      this.taxonomy.topics = (results[1] && results[1].results) || [];
    } catch (e) {
      this.taxonomy.lifeEvents = [];
      this.taxonomy.topics = [];
    }
    this.readUrl();
    this.renderSidebar();
    await this.loadResults(true);
  };

  LAResourceFilter.prototype.bindStaticEvents = function () {
    var self = this;
    if (this.sortEl) {
      this.sortEl.addEventListener("change", function () {
        self.sortKey = self.sortEl.value in SORTS ? self.sortEl.value : "recommended";
        self.updateUrl();
        self.loadResults(true);
      });
    }
    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener("click", function () { self.loadResults(false); });
    }
    if (this.clearEl) {
      this.clearEl.addEventListener("click", function () { self.clearAll(); });
    }
    // Mobile bottom-sheet open/close.
    if (this.triggerEl) {
      this.triggerEl.addEventListener("click", function () { self.openSheet(); });
    }
    if (this.doneEl) {
      this.doneEl.addEventListener("click", function () { self.closeSheet(); });
    }
    if (this.backdropEl) {
      this.backdropEl.addEventListener("click", function () { self.closeSheet(); });
    }
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && self.sheetOpen) self.closeSheet();
    });
    this.bindSheetDrag();
    // Favorite toggling within the cards container.
    if (this.cardsEl) {
      this.cardsEl.addEventListener("click", function (e) {
        var btn = e.target.closest(".favorite-btn");
        if (btn && self.cardsEl.contains(btn)) {
          e.preventDefault();
          toggleFavorite(btn);
        }
      });
    }
  };

  // Build the left sidebar from the taxonomy.
  LAResourceFilter.prototype.renderSidebar = function () {
    if (!this.groupsEl) return;
    var html = "";
    html += this.renderTopicGroup();
    html += this.renderLifeEventGroup();
    html += this.renderTypeGroup();
    this.groupsEl.innerHTML = html;
    this.bindSidebarEvents();
    this.syncCheckboxes();
    this.updateClearVisibility();
  };

  LAResourceFilter.prototype.groupShell = function (key, title, bodyHtml, empty) {
    return (
      '<section class="la-rf__group" data-group="' + key + '">' +
      '<button type="button" class="la-rf__group-head" aria-expanded="true" data-la-rf-toggle>' +
      '<span>' + esc(title) + "</span>" + CHEVRON +
      "</button>" +
      '<div class="la-rf__group-body">' +
      (empty ? '<p class="la-rf__muted">Nothing here yet.</p>' : bodyHtml) +
      "</div>" +
      "</section>"
    );
  };

  LAResourceFilter.prototype.checkboxRow = function (dim, id, label) {
    var cid = "rf-" + dim + "-" + bareId(id);
    return (
      '<label class="la-rf__opt" for="' + esc(cid) + '">' +
      '<input type="checkbox" id="' + esc(cid) + '" data-dim="' + dim + '" value="' + esc(id) + '">' +
      "<span>" + esc(label) + "</span>" +
      "</label>"
    );
  };

  LAResourceFilter.prototype.renderTopicGroup = function () {
    var rows = (this.taxonomy.topics || [])
      .map(function (t) { return this.checkboxRow("topic", t.id, t.title || t.value); }, this)
      .join("");
    return this.groupShell("topic", "By topic", '<div class="la-rf__opts">' + rows + "</div>", rows === "");
  };

  LAResourceFilter.prototype.renderLifeEventGroup = function () {
    // Flat list of life events — sub-topics are intentionally not shown here.
    var rows = (this.taxonomy.lifeEvents || [])
      .map(function (ev) { return this.checkboxRow("life_event", ev.id, ev.title || ev.value); }, this)
      .join("");
    return this.groupShell("life_event", "By life event", '<div class="la-rf__opts">' + rows + "</div>", rows === "");
  };

  LAResourceFilter.prototype.renderTypeGroup = function () {
    var rows =
      this.checkboxRow("type", "articles", "Articles") +
      this.checkboxRow("type", "products", "Offerings");
    return this.groupShell("type", "By resource type", '<div class="la-rf__opts">' + rows + "</div>", false);
  };

  LAResourceFilter.prototype.bindSidebarEvents = function () {
    var self = this;
    // Collapse/expand groups.
    this.groupsEl.querySelectorAll("[data-la-rf-toggle]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var group = btn.closest(".la-rf__group");
        var open = group.classList.toggle("is-collapsed");
        btn.setAttribute("aria-expanded", open ? "false" : "true");
      });
    });
    // Expand/collapse a life event's sub-topics.
    this.groupsEl.querySelectorAll("[data-la-rf-sub-toggle]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var ev = btn.closest(".la-rf__event");
        var subs = ev.querySelector(".la-rf__subs");
        if (!subs) return;
        var show = subs.hasAttribute("hidden");
        if (show) subs.removeAttribute("hidden"); else subs.setAttribute("hidden", "");
        btn.setAttribute("aria-expanded", show ? "true" : "false");
        ev.classList.toggle("is-open", show);
      });
    });
    // Checkbox changes drive the filter.
    this.groupsEl.querySelectorAll('input[type="checkbox"][data-dim]').forEach(function (cb) {
      cb.addEventListener("change", function () { self.onCheckbox(cb); });
    });
  };

  LAResourceFilter.prototype.setFor = function (dim) {
    if (dim === "type") return this.types;
    if (dim === "life_event") return this.lifeEvents;
    if (dim === "sub_topic") return this.subTopics;
    if (dim === "topic") return this.topics;
    return null;
  };

  LAResourceFilter.prototype.onCheckbox = function (cb) {
    var set = this.setFor(cb.dataset.dim);
    if (!set) return;
    var val = cb.dataset.dim === "type" ? cb.value : toGid(cb.value);
    if (cb.checked) set.add(val); else set.delete(val);
    this.updateClearVisibility();
    this.updateUrl();
    this.loadResults(true);
  };

  LAResourceFilter.prototype.syncCheckboxes = function () {
    var self = this;
    this.groupsEl.querySelectorAll('input[type="checkbox"][data-dim]').forEach(function (cb) {
      var set = self.setFor(cb.dataset.dim);
      var val = cb.dataset.dim === "type" ? cb.value : toGid(cb.value);
      cb.checked = !!(set && set.has(val));
      // Keep a life event's sub-topics visible when any is selected.
      if (cb.dataset.dim === "sub_topic" && cb.checked) {
        var ev = cb.closest(".la-rf__event");
        if (ev) {
          var subs = ev.querySelector(".la-rf__subs");
          if (subs) subs.removeAttribute("hidden");
          var t = ev.querySelector("[data-la-rf-sub-toggle]");
          if (t) t.setAttribute("aria-expanded", "true");
          ev.classList.add("is-open");
        }
      }
    });
  };

  LAResourceFilter.prototype.updateClearVisibility = function () {
    var any = this.types.size || this.lifeEvents.size || this.subTopics.size || this.topics.size;
    if (this.clearEl) this.clearEl.hidden = !any;
    this.updateActiveCount();
  };

  // ---- mobile bottom-sheet -------------------------------------------------
  LAResourceFilter.prototype.updateActiveCount = function () {
    if (!this.activeCountEl) return;
    var n = this.types.size + this.lifeEvents.size + this.subTopics.size + this.topics.size;
    this.activeCountEl.textContent = n ? String(n) : "";
    this.activeCountEl.classList.toggle("is-active", n > 0);
  };

  LAResourceFilter.prototype.openSheet = function () {
    if (!this.sidebarEl) return;
    this.sheetOpen = true;
    this.sidebarEl.style.transition = "";
    this.sidebarEl.style.transform = "";
    this.sidebarEl.classList.add("is-open");
    if (this.backdropEl) this.backdropEl.classList.add("is-open");
    if (this.triggerEl) this.triggerEl.setAttribute("aria-expanded", "true");
    this.sidebarEl.setAttribute("aria-modal", "true");
    this._prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  };

  LAResourceFilter.prototype.closeSheet = function () {
    if (!this.sidebarEl) return;
    this.sheetOpen = false;
    this.sidebarEl.style.transform = ""; // let the CSS class drive the slide-down
    this.sidebarEl.classList.remove("is-open");
    if (this.backdropEl) this.backdropEl.classList.remove("is-open");
    if (this.triggerEl) this.triggerEl.setAttribute("aria-expanded", "false");
    this.sidebarEl.setAttribute("aria-modal", "false");
    document.body.style.overflow = this._prevOverflow || "";
  };

  // Drag the grab-handle downward to dismiss (touch + mouse), mirroring la-filter.
  LAResourceFilter.prototype.bindSheetDrag = function () {
    var self = this;
    var handle = this.handleEl;
    var sheet = this.sidebarEl;
    if (!handle || !sheet) return;
    var dragging = false, startY = 0, dy = 0, h = 1;

    function start(y) {
      dragging = true; startY = y; dy = 0;
      h = sheet.offsetHeight || 1;
      sheet.style.transition = "none";
    }
    function move(y) {
      if (!dragging) return;
      dy = Math.max(0, y - startY);
      sheet.style.transform = "translateY(" + dy + "px)";
    }
    function end() {
      if (!dragging) return;
      dragging = false;
      sheet.style.transition = "";
      if (dy > h * 0.25) {
        self.closeSheet();
      } else {
        sheet.style.transform = ""; // snap back to open position
      }
      dy = 0;
    }

    handle.addEventListener("touchstart", function (e) { start(e.touches[0].clientY); }, { passive: true });
    document.addEventListener("touchmove", function (e) { if (dragging) move(e.touches[0].clientY); }, { passive: true });
    document.addEventListener("touchend", end);
    handle.addEventListener("mousedown", function (e) { e.preventDefault(); start(e.clientY); });
    document.addEventListener("mousemove", function (e) { if (dragging) move(e.clientY); });
    document.addEventListener("mouseup", end);
  };

  LAResourceFilter.prototype.clearAll = function () {
    this.types.clear();
    this.lifeEvents.clear();
    this.subTopics.clear();
    this.topics.clear();
    this.syncCheckboxes();
    this.updateClearVisibility();
    this.updateUrl();
    this.loadResults(true);
  };

  // ---- query building + fetch ----------------------------------------------
  LAResourceFilter.prototype.buildBody = function () {
    var body = {
      content_type: this.types.size ? Array.from(this.types) : CONTENT_TYPES,
    };
    if (this.lifeEvents.size) body.life_event_ids = Array.from(this.lifeEvents);
    if (this.subTopics.size) body.sub_topic_ids = Array.from(this.subTopics);
    if (this.topics.size) body.topic_ids = Array.from(this.topics);
    var s = SORTS[this.sortKey] || SORTS.recommended;
    body.sort_field = s.sort_field;
    body.sort_direction = s.sort_direction;
    body.limit = this.pageSize;
    body.offset = this.offset;
    return body;
  };

  LAResourceFilter.prototype.renderSkeletonCards = function () {
    if (!this.cardsEl) return;
    var n = Math.min(this.pageSize, 9);
    var s = "";
    for (var i = 0; i < n; i++) s += '<div class="la-rf__skeleton" aria-hidden="true"></div>';
    this.cardsEl.innerHTML = s;
  };

  LAResourceFilter.prototype.loadResults = async function (reset) {
    if (this.loading) return;
    this.loading = true;
    if (reset) {
      this.offset = 0;
      this.total = null;
      this.renderSkeletonCards();
      if (this.emptyEl) this.emptyEl.hidden = true;
    } else if (this.loadMoreBtn) {
      this.loadMoreBtn.disabled = true;
      this.loadMoreBtn.textContent = "Loading…";
    }
    try {
      var resp = await postFilter(this.buildBody());
      var items = resp.results || [];
      this.total = typeof resp.total_count === "number" ? resp.total_count : this.offset + items.length;

      if (reset) this.cardsEl.innerHTML = "";
      if (this.offset === 0 && items.length === 0) {
        if (this.emptyEl) this.emptyEl.hidden = false;
      } else {
        if (this.emptyEl) this.emptyEl.hidden = true;
        this.cardsEl.insertAdjacentHTML("beforeend", renderCardsHtml(items));
      }
      this.offset += items.length;

      if (this.countEl) {
        this.countEl.textContent = this.total === 1 ? "1 result" : this.total + " results";
      }
      if (this.paginationEl && this.loadMoreBtn) {
        this.loadMoreBtn.disabled = false;
        this.loadMoreBtn.textContent = "Load more";
        if (this.offset >= this.total || items.length === 0) {
          this.paginationEl.hidden = true;
        } else {
          this.paginationEl.hidden = false;
        }
      }
      if (debugOn()) {
        console.log("[la-resource-filter]", JSON.stringify(this.buildBody()), "->", resp.total_count, resp.type_counts);
      }
    } catch (err) {
      if (this.offset === 0) {
        this.cardsEl.innerHTML = "";
        if (this.emptyEl) {
          this.emptyEl.hidden = false;
          this.emptyEl.textContent = "Resources are unavailable right now.";
        }
      }
      if (this.loadMoreBtn) { this.loadMoreBtn.disabled = false; this.loadMoreBtn.textContent = "Load more"; }
      console.error("[la-resource-filter] load failed", err);
    } finally {
      this.loading = false;
    }
  };

  // ---- URL sync (shareable state) ------------------------------------------
  LAResourceFilter.prototype.updateUrl = function () {
    var params = new URLSearchParams(window.location.search);
    function put(key, set, bare) {
      var vals = Array.from(set).map(function (v) { return bare ? bareId(v) : v; });
      if (vals.length) params.set(key, vals.join(",")); else params.delete(key);
    }
    put("topics", this.topics, true);
    put("life_events", this.lifeEvents, true);
    put("sub_topics", this.subTopics, true);
    put("types", this.types, false);
    if (this.sortKey && this.sortKey !== "recommended") params.set("sort", this.sortKey);
    else params.delete("sort");
    var qs = params.toString();
    var url = window.location.pathname + (qs ? "?" + qs : "") + window.location.hash;
    window.history.replaceState(null, "", url);
  };

  LAResourceFilter.prototype.readUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var self = this;
    function take(key, set, gid) {
      var raw = params.get(key);
      if (!raw) return;
      raw.split(",").map(function (s) { return s.trim(); }).filter(Boolean).forEach(function (v) {
        set.add(gid ? toGid(v) : v);
      });
    }
    take("topics", this.topics, true);
    take("life_events", this.lifeEvents, true);
    take("sub_topics", this.subTopics, true);
    take("types", this.types, false);
    var sort = params.get("sort");
    if (sort && sort in SORTS) {
      this.sortKey = sort;
      if (this.sortEl) this.sortEl.value = sort;
    }
  };

  function init() {
    document.querySelectorAll("[data-la-rf-root]").forEach(function (root) {
      if (root.__laRf) return;
      root.__laRf = true;
      new LAResourceFilter(root).init();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
